import { Rank, type EnemyDef } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { CYCLE, firstDelayAt, PHASE_AILES, PHASE_DOME, PHASE_SOCLE } from '../bosses/colosse.cycle';
import { defineEnemy, Motion, Pattern } from '../schema';

/* ═══════════════════════════════════════════════════════════════════════════
   B03-a/b/c — LES SECTIONS DU COLOSSE.

   Trois définitions dans un fichier, comme pour les tourelles du Bastion : ce
   qui compte est le RAPPORT entre elles — leur coût respectif et ce que chacune
   retire du combat — et ce rapport se lit mal éclaté sur trois fichiers.

   ── Le tableau de décision qu'elles composent ──

     section  PV      ce que sa destruction retire
     ──────────────────────────────────────────────────────────────────────
     aile     8 000   la moitié du feu croisé statique. Deux ailes = 16 000 PV
              (×2)    pour rendre tout le terrain latéral. Le plus cher, et le
                      plus rentable si l'on compte tenir longtemps.
     dôme     6 500   LA seule menace visée du combat. La seule section dont la
                      destruction change la NATURE de ce qu'on esquive au lieu
                      de son volume.
     socle    3 000   l'anneau lent. Le moins cher, et le moins utile — c'est
                      la voie de la panique, celle qu'on prend quand on tire
                      sur ce qui est devant soi.

     Valeurs de base ; le monde 3 les multiplie par 2,4. Les quatre sections
     réunies pèsent 25 500, soit un tiers du noyau : démonter entièrement le
     Colosse coûte donc un tiers de temps en plus que l'ignorer, ce qui est
     précisément le prix qu'on veut faire payer à la voie sûre.

   Le noyau n'étant pas blindé, ces trois lignes ne sont pas un ordre imposé
   mais un menu. Un joueur qui a compris son build sait laquelle il peut se
   permettre d'ignorer ; c'est exactement ce que le combat mesure.
   ═══════════════════════════════════════════════════════════════════════════ */

const BASE = {
  // Standard et non Heavy malgré les points de vie : le rang ne mesure pas la
  // résistance, il dose le gel et la secousse à la mort (PLAN.md §6.2). Quatre
  // sections mourant avec un hitstop lourd chacune hacheraient le combat.
  rank: Rank.Standard,
  telegraph: 2.4,
  // Les parties sont repositionnées chaque tick sur leur porteur : leur propre
  // trajectoire n'est jamais utilisée.
  motion: { kind: Motion.Hover, speed: 0, amplitude: 0, frequency: 0, holdY: 320 },
} as const;

const sections: EnemyDef[] = [
  defineEnemy({
    ...BASE,
    id: 'colosse-aile',
    hp: 8000,
    visualRadius: 32,
    score: 4200,
    drops: 3,
    emitter: {
      /* Éventail statique orienté vers le bas et large : deux ailes à ±210 u
         produisent un feu CROISÉ qui se referme au centre. C'est ce qui punit
         le joueur restant sous le boss — et donc ce qui rend le balancement du
         porteur lisible, puisque la zone sûre se déplace avec lui. */
      pattern: Pattern.Fan,
      every: CYCLE,
      count: 7,
      spreadDeg: 74,
      speed: 360,
      firstDelay: firstDelayAt(PHASE_AILES),
      family: Fam.EnemyPurple,
    },
  }),
  defineEnemy({
    ...BASE,
    id: 'colosse-dome',
    hp: 6500,
    visualRadius: 30,
    score: 3800,
    drops: 4,
    emitter: {
      /* La salve visée, tirée au sortir du verrou. Resserrée (16°) et rapide :
         c'est la seule chose du combat qui suive le joueur, et la seule qui
         exige une esquive au bon instant plutôt qu'un bon placement.

         Cinq projectiles laissent toujours un passage, y compris à bout
         portant. Une salve insurvivable transformerait le télégraphe en
         compte à rebours avant une mort — le joueur verrait arriver ce qu'il
         ne peut pas éviter, ce qui est la pire forme de difficulté. */
        pattern: Pattern.Aimed,
      every: CYCLE,
      count: 5,
      spreadDeg: 16,
      speed: 500,
      firstDelay: firstDelayAt(PHASE_DOME),
      family: Fam.EnemyPink,
    },
  }),
  defineEnemy({
    ...BASE,
    id: 'colosse-socle',
    hp: 3000,
    visualRadius: 26,
    score: 1800,
    drops: 3,
    emitter: {
      // Anneau lent : le volume, et la pulsation qui donne au Colosse sa
      // silhouette vivante. `stepDeg` décale chaque anneau pour qu'aucune place
      // sûre ne soit mémorisable d'un cycle à l'autre.
      pattern: Pattern.Ring,
      every: CYCLE,
      count: 16,
      spreadDeg: 0,
      speed: 290,
      stepDeg: 9,
      firstDelay: firstDelayAt(PHASE_SOCLE),
      family: Fam.EnemyRed,
    },
  }),
];

export default sections;
